import { type CommandHandler } from '@telespot/core';

import { UpdateAlgorithmMetadata } from './update-algorithm-metadata.command';
import { Algorithm, type AlgorithmRepository } from '@telespot/domain';

/**
 * @group Protocols
 * @category Errors
 */
export class UpdateAlgorithmMetadataError extends Error {
  constructor(public readonly algorithmId: string) {
    super(`Update algorithm metadata error: algorithm not found with id ${algorithmId}`);
  }
}

export type IUpdateAlgorithmMetadataHandler = CommandHandler<UpdateAlgorithmMetadata>;

/**
 * @group Protocols
 * @category Handlers
 */
export class UpdateAlgorithmMetadataHandler implements IUpdateAlgorithmMetadataHandler {
  constructor(private readonly algorithmRepository: AlgorithmRepository) {}

  private async getAlgorithm(algorithmId: string): Promise<Algorithm> {
    try {
      return await this.algorithmRepository.getById(algorithmId);
    } catch (error) {
      throw new UpdateAlgorithmMetadataError(algorithmId);
    }
  }

  async execute(command: UpdateAlgorithmMetadata): Promise<void> {
    const { algorithmId, name, description, version, regulated } = command.details;

    const algorithm = await this.getAlgorithm(algorithmId);

    if (name !== undefined) algorithm.updateName(name);
    if (description !== undefined) algorithm.updateDescription(description);
    if (version !== undefined) algorithm.updateVersion(version);
    if (regulated !== undefined) algorithm.changeRegulatedStatus(regulated);

    await this.algorithmRepository.saveAlgorithm(algorithm);
  }
}
